import React, { forwardRef, JSX } from 'react';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { Box, BoxProps } from '../Box';
import { Icon } from '../Icon';
import { mergeClassAttributes } from '../../helpers/classes';
import {
  textWarningText,
  textPositiveText,
  textNegativeText,
  textGray600,
} from '../../styles/text';
import {
  bgWarningLight,
  bgPositiveLight,
  bgNegativeLightest,
  bg100,
} from '../../styles';

export enum AlertVariant {
  default = 'default',
  danger = 'danger',
  success = 'success',
  warning = 'warning',
}

export type AlertProps = BoxProps & {
  children: React.ReactNode;
  variant?: AlertVariant;
  className?: string;
  cn?: string;
};

const variantColors = {
  [AlertVariant.warning]: `${textWarningText} ${bgWarningLight}`,
  [AlertVariant.danger]: `${textNegativeText} ${bgNegativeLightest}`,
  [AlertVariant.success]: `${textPositiveText} ${bgPositiveLight}`,
  [AlertVariant.default]: `${textGray600} ${bg100}`,
};

const variantIcons = {
  [AlertVariant.warning]: faTriangleExclamation,
  [AlertVariant.danger]: faTriangleExclamation,
  [AlertVariant.success]: faCircleCheck,
  [AlertVariant.default]: faCircleInfo,
};

/**
 * Returns icon by variant.
 */
export const getIcon = (variant: AlertVariant) => variantIcons[variant];

/**
 * Returns color classes by variant.
 */
export const getText = (variant: AlertVariant) => variantColors[variant];

export const Alert = forwardRef((props: AlertProps, ref): JSX.Element => {
  const {
    children,
    variant = AlertVariant.default,
    className,
    cn,
    ...otherProps
  } = props;
  const colorClasses = getText(variant);

  const classes = ['font-bold', 'p-4', 'rounded-lg', colorClasses];

  return (
    <Box
      {...otherProps}
      className={mergeClassAttributes({ className, cn }, classes)}
      data-testid={`Alert-${variant}`}
    >
      <Icon
        size="sm"
        icon={getIcon(variant)}
        className="float-left mr-1 mt-[5px]"
      />
      {children}
    </Box>
  );
});

Alert.displayName = 'Alert';

export default Alert;
